import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    FlatList,
    ListRenderItem,
    Platform,
    ScrollView,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import {
    Edge,
    SafeAreaView,
    useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import debounce from 'lodash.debounce';

import { CloseIcon } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    PROFILE_CLOSE_BUTTON,
    PROFILE_CLOSE_BUTTON_EDIT_MODE,
    PROFILE_MANAGE_BUTTON,
    PROFILE_SELECTION_DESCRIPTION,
    PROFILE_SELECTION_TITLE,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { setInitialWebPathName } from 'store/config/actions';
import {
    selectAppState,
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import {
    endSentryTransaction,
    startSentryTransaction,
} from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import {
    changeUserProfile,
    deleteProfileRequestReset,
    editProfileRequestReset,
    getEntitlements,
    getPinsStatus,
    getProfilesList,
    setCurrentProfileAvatar,
    setSuspensionType,
    updateProfileRelatedData,
} from 'store/user/actions';
import {
    ParentalControlRating,
    Profile,
    SUSPENSION_TYPES,
} from 'store/user/reducer.types';
import {
    selectActiveUserProfile,
    selectChangeProfileErrorCode,
    selectChangeProfileErrorStatus,
    selectDeleteProfileErrorCode,
    selectDeleteProfileStatus,
    selectEditProfileErrorCode,
    selectEditProfileStatus,
    selectIfCollectionSuspensionActive,
    selectIfKidsProfileActive,
    selectProfileListErrorCode,
    selectProfileListErrorStatus,
    selectProfilesList,
} from 'store/user/selectors';
import { RequestStatus } from 'store/utils';

import { RegularStorage, StorageKeys } from 'services/mmkv';
import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import getBase64Image from 'utils/getBase64Image';
import useBackHandler from 'utils/hooks/useBackHandler.ts';
import useNoInternet from 'utils/hooks/useNoInternet';
import usePrevious from 'utils/hooks/usePrevious';
import useSafeAreaEdges from 'utils/hooks/useSafeAreaEdges';
import { isPhone, isTabletOrWeb, isWeb } from 'utils/responsiveness';

import {
    DIALOG_ERR_GENERIC_BODY,
    DIALOG_ERR_GENERIC_TITLE,
} from 'screens/Collection/constants';
import useParentalControl from 'screens/PlayerModal/hooks/useParentalControl';

import SvgButton from 'components/CustomButtons/SvgButton';
import DialogBody from 'components/Dialog/components/DialogBody';
import ErrorDialog from 'components/ErrorDialog';
import {
    AspectRatio,
    ImageContentType,
    ImageType,
} from 'components/NagraImage/types';
import { getImageUrl } from 'components/NagraImage/utils';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import GenericPinDialog from '../../components/GenericPinDialog';
import ProfileItem from './components/ProfileItem';
import useUpdateProfileSettings from './hooks/useUpdateProfileSettings';
import styles, { PHONE_TOP_PADDING } from './styles';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.PROFILES>;
};

const {
    PROFILE: {
        PROFILE_PROFILE_SELECTION_TITLE,
        PROFILE_PROFILE_SELECTION_DESCRIPTION,
        PROFILE_PROFILE_SELECTION_ADD_PROFILE,
        MANAGE_BUTTON,
        PROFILE_ERROR_PROFILE_ALREADY_DELETED_TITLE,
        PROFILE_ERROR_PROFILE_ALREADY_DELETED_DESCRIPTION,
    },
    CLOSE_BUTTON,
    TRY_AGAIN_BUTTON,
    OK_BUTTON,
    ACCESSIBILITY: {
        LOGIN: { ACCESSIBILITY_CLOSE_BUTTON },
    },
} = LANGUAGE_KEYS;

const EMPTY_PROFILE = {
    name: PROFILE_PROFILE_SELECTION_ADD_PROFILE,
    _id: '',
    pinPromptRequired: false,
    iconName: 'default',
    childProfile: false,
    accountId: '',
    default: false,
    maxAgeRating: 0,
    created: '',
    modified: '',
    ratingsThresholds: [
        {
            ratingsBody: 'HCRS',
            ratingsThreshold: ParentalControlRating.HU18,
        },
    ],
};

export const MAX_PROFILES = 5;

function ProfilesPage({ navigation }: Props) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { top } = useSafeAreaInsets();
    const { primary } = useSelector(selectLayoutConfig);
    const profiles = useSelector(selectProfilesList);
    const selectedProfileId = useSelector(selectActiveUserProfile);
    const appState = useSelector(selectAppState);
    const profileListErrorCode = useSelector(selectProfileListErrorCode);
    const profileListErrorStatus = useSelector(selectProfileListErrorStatus);
    const changeProfileErrorCode = useSelector(selectChangeProfileErrorCode);
    const changeProfileErrorStatus = useSelector(
        selectChangeProfileErrorStatus,
    );
    const editProfileRequestStatus = useSelector(selectEditProfileStatus);
    const editProfileRequestCode = useSelector(selectEditProfileErrorCode);
    const deleteProfileRequestStatus = useSelector(selectDeleteProfileStatus);
    const deleteProfileRequestCode = useSelector(selectDeleteProfileErrorCode);
    const isKidsProfileActive = useSelector(selectIfKidsProfileActive);
    const collectionSuspensionActive = useSelector(
        selectIfCollectionSuspensionActive,
    );
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const edges: Edge[] = useSafeAreaEdges();
    const { isInternetConnected } = useNoInternet();
    const prevIsInternetConnected = usePrevious(isInternetConnected);
    const selectedProfileIdScreenBlurRef = useRef<string | undefined>(
        undefined,
    );

    const [isFTIFlow, setIsFTIFlow] = useState(true);
    const [newSelectedProfileId, setNewSelectedProfileId] = useState('');
    const [editProfileName, setEditProfileName] = useState('');
    const [isManageFlowEnabled, setIsManageFlowEnabled] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showProfileChangeErrorDialog, setShowProfileChangeErrorDialog] =
        useState(false);

    const {
        showPinDialog,
        onShowPinDialog,
        onParentalCheckDismiss,
        onParentalControlChecked,
    } = useParentalControl({
        fullscreen: false,
        onSetPlayerError: () => null,
        onResetPlayerError: () => null,
    });

    useUpdateProfileSettings(isFTIFlow);

    const debouncedProfilesFetch = useCallback(
        debounce(() => dispatch(getProfilesList()), 300, {
            leading: true,
            trailing: false,
        }),
        [],
    );

    const isRootNavigator = useMemo(
        () => !navigation.canGoBack(),
        [navigation],
    );

    const profileData = useMemo(
        () =>
            profiles?.length &&
            profiles.length < MAX_PROFILES &&
            !isManageFlowEnabled &&
            !isKidsProfileActive
                ? [...profiles, EMPTY_PROFILE]
                : profiles,
        [profiles, isManageFlowEnabled, isKidsProfileActive],
    );

    const selectedProfile = useMemo(
        () => profiles?.find(({ _id }) => _id === selectedProfileId),
        [profiles, selectedProfileId],
    );

    const activeButtonText = useMemo(
        () => (isManageFlowEnabled ? CLOSE_BUTTON : MANAGE_BUTTON),
        [isManageFlowEnabled],
    );

    const activeButtonTestId = useMemo(
        () =>
            isManageFlowEnabled
                ? PROFILE_CLOSE_BUTTON_EDIT_MODE
                : PROFILE_MANAGE_BUTTON,
        [isManageFlowEnabled],
    );

    const errorKeys = useMemo(() => {
        if (changeProfileErrorCode === 4705) {
            return {
                title: PROFILE_ERROR_PROFILE_ALREADY_DELETED_TITLE,
                description: PROFILE_ERROR_PROFILE_ALREADY_DELETED_DESCRIPTION,
                actionButtonText: undefined,
            };
        }

        return {
            title: DIALOG_ERR_GENERIC_TITLE,
            description: DIALOG_ERR_GENERIC_BODY,
            actionButtonText: TRY_AGAIN_BUTTON,
        };
    }, [changeProfileErrorCode]);

    const handleGetFTIIndicator = useCallback(async () => {
        const profilesFTIShown = await RegularStorage.getItem(
            StorageKeys.FTI_PROFILES_SHOWN,
        );

        setIsFTIFlow(!profilesFTIShown);
    }, []);

    const handleSetFTIIndicator = useCallback(async () => {
        await RegularStorage.setItem(
            StorageKeys.FTI_PROFILES_SHOWN,
            JSON.stringify(true),
        );
    }, []);

    const handleClose = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    const handleUpdateSelectedProfileAvatar = useCallback(
        async (iconName: string) => {
            const uri = getImageUrl({
                contentId: iconName,
                contentType: ImageContentType.PROFILE,
                imageType: ImageType.AVATAR,
                aspect: AspectRatio.PERSON,
                height: 32,
                width: 32,
            });
            const imageUri = iconName === 'default' || !iconName ? '' : uri;

            const base64 = await getBase64Image(imageUri);

            dispatch(setCurrentProfileAvatar(base64 as string));
        },
        [],
    );

    const handleSuccessSelectProfile = useCallback(
        async (newProfileId: string) => {
            try {
                const newProfileWasSelected =
                    newProfileId !== selectedProfileId ||
                    (selectedProfileIdScreenBlurRef.current &&
                        selectedProfileIdScreenBlurRef.current !==
                            newProfileId);

                if (isWeb) {
                    const newUserProfile = profiles.find(
                        ({ _id }) => _id === newProfileId,
                    );

                    if (newUserProfile) {
                        await handleUpdateSelectedProfileAvatar(
                            newUserProfile.iconName,
                        );
                    }
                }

                if (isRootNavigator) {
                    if (newProfileWasSelected) {
                        dispatch(
                            setInitialWebPathName({
                                pathName: '',
                                query: undefined,
                            }),
                        );
                    }

                    navigation.replace(SCREEN.PERSONALIZE_SCREEN);

                    handleSetFTIIndicator();
                } else if (newProfileWasSelected) {
                    await new Promise((resolve, reject) => {
                        dispatch(updateProfileRelatedData(resolve, reject));
                    });

                    navigation.reset({
                        routes: [{ name: SCREEN.ROOT }],
                    });
                } else {
                    dispatch(getEntitlements({ autoRetry: true }));
                    handleClose();
                }
            } catch (error) {
                setShowProfileChangeErrorDialog(true);
            }
        },
        [
            isRootNavigator,
            handleSetFTIIndicator,
            profiles,
            handleClose,
            handleUpdateSelectedProfileAvatar,
            selectedProfileId,
        ],
    );

    const handleRetryProfileListFetch = useCallback(() => {
        setShowErrorDialog(false);
        debouncedProfilesFetch();
    }, [dispatch, debouncedProfilesFetch]);

    const handleCloseErrorDialog = useCallback(() => {
        if (changeProfileErrorCode === 4705) {
            handleRetryProfileListFetch();
        } else {
            handleClose();
        }

        setShowErrorDialog(false);
    }, [handleClose, changeProfileErrorCode, handleRetryProfileListFetch]);

    const handleCloseProfileChangeError = useCallback(() => {
        setShowProfileChangeErrorDialog(false);
    }, []);

    const handleSelectProfileItem = useCallback(
        (id: string) => {
            dispatch(
                startSentryTransaction(SENTRY_TRANSACTIONS.MainPageRendering),
            );

            const retryChangeProfile = () =>
                new Promise<void>((resolve, reject) => {
                    dispatch(
                        changeUserProfile(id, {
                            onSuccess: () => {
                                handleSuccessSelectProfile(id);

                                resolve();
                            },
                            onFailure: (error) => {
                                if (isErrorNeedsRetry(error)) {
                                    reject(error);
                                } else {
                                    onParentalControlChecked();
                                    setShowErrorDialog(true);

                                    resolve();
                                }
                            },
                        }),
                    );
                });

            dispatch(
                changeUserProfile(id, {
                    onSuccess: () => {
                        handleSuccessSelectProfile(id);
                    },
                    onFailure: (error) => {
                        if (isErrorNeedsRetry(error)) {
                            RetryController.showVitalDialog({
                                actionButtonTitle: LANGUAGE_KEYS.RELOAD_BUTTON,
                                actionButtonOnPress: retryChangeProfile,
                            });
                            RetryController.registerRetryCallback(
                                retryChangeProfile,
                                {
                                    retryDuration: 10 * 60 * 1000,
                                    maxRetryCount: 10,
                                },
                            );
                            RetryController.executeRetry();
                        } else {
                            onParentalControlChecked();
                            setShowErrorDialog(true);
                        }
                    },
                }),
            );
        },
        [handleSuccessSelectProfile, onParentalControlChecked, dispatch],
    );

    const handleGetPinStatus = useCallback(() => {
        const retryPinsStatusAction = () =>
            new Promise<void>((resolve, reject) => {
                dispatch(
                    getPinsStatus({
                        onSuccess: () => {
                            onShowPinDialog();
                        },
                        onFailure: (error) => {
                            if (isErrorNeedsRetry(error)) {
                                reject(error);
                            } else {
                                resolve();
                                setShowErrorDialog(true);
                            }
                        },
                    }),
                );
            });

        dispatch(
            getPinsStatus({
                onSuccess: () => {
                    onShowPinDialog();
                },
                onFailure: (error) => {
                    if (isErrorNeedsRetry(error)) {
                        RetryController.showVitalDialog({
                            actionButtonTitle: LANGUAGE_KEYS.CANCEL_BUTTON,
                            actionButtonOnPress: () => {
                                RetryController.hideVitalDialog();
                            },
                        });
                        RetryController.registerRetryCallback(
                            retryPinsStatusAction,
                            {
                                retryDuration: 10 * 60 * 1000,
                                maxRetryCount: 10,
                            },
                        );
                        RetryController.executeRetry();
                    } else {
                        setShowErrorDialog(true);
                    }
                },
            }),
        );
    }, []);

    const handleProfileItemPress = useCallback(
        (id: string) => {
            if (id) {
                const newSelectedProfile = profiles.find(
                    ({ _id }) => _id === id,
                );

                if (isManageFlowEnabled && newSelectedProfile?.name) {
                    onShowPinDialog();
                    setEditProfileName(newSelectedProfile?.name);
                } else if (
                    selectedProfile?.childProfile &&
                    selectedProfile?.childProfile !==
                        newSelectedProfile?.childProfile
                ) {
                    setNewSelectedProfileId(id);
                    handleGetPinStatus();
                } else {
                    handleSelectProfileItem(id);
                }
            } else {
                handleGetPinStatus();
            }
        },
        [
            dispatch,
            handleClose,
            isRootNavigator,
            navigation,
            handleSetFTIIndicator,
            selectedProfile,
            profiles,
            handleSelectProfileItem,
            isManageFlowEnabled,
            onShowPinDialog,
        ],
    );

    const handleActionButtonPress = useCallback(() => {
        if (isManageFlowEnabled) {
            setIsManageFlowEnabled(false);
        } else {
            setIsManageFlowEnabled(true);
        }
    }, [isManageFlowEnabled]);

    const handlePinControlChecked = useCallback(() => {
        if (newSelectedProfileId) {
            handleSelectProfileItem(newSelectedProfileId);
        } else if (isManageFlowEnabled) {
            navigation.push(SCREEN.MANAGE_PROFILE, {
                name: editProfileName,
            });
            setTimeout(onParentalControlChecked, 150);
        } else {
            navigation.push(SCREEN.PROFILE_TYPE);
            setTimeout(onParentalControlChecked, 150);
        }
    }, [
        onParentalControlChecked,
        newSelectedProfileId,
        handleSelectProfileItem,
        navigation,
        isManageFlowEnabled,
        editProfileName,
    ]);

    const handlePinControlClose = useCallback(() => {
        setNewSelectedProfileId('');

        onParentalCheckDismiss();
    }, [onParentalCheckDismiss]);

    const handleHardwareBackPress = useCallback(() => {
        if (isManageFlowEnabled) {
            setIsManageFlowEnabled(false);
            return true;
        }

        return !(selectedProfile && navigation.canGoBack());
    }, [isManageFlowEnabled, navigation, selectedProfile]);

    const renderProfileItem: ListRenderItem<Profile> = useCallback(
        ({ item, index }) => (
            <ProfileItem
                id={item._id}
                iconName={item.iconName}
                name={item.name}
                isChildProfile={item.childProfile}
                isSelected={!isFTIFlow && item._id === selectedProfileId}
                onPress={handleProfileItemPress}
                index={index}
                isEditEnabled={isManageFlowEnabled}
            />
        ),
        [
            selectedProfileId,
            handleProfileItemPress,
            isFTIFlow,
            isManageFlowEnabled,
        ],
    );

    const renderProfiles = useCallback(
        () => (
            <FlatList
                data={profileData}
                renderItem={renderProfileItem}
                keyExtractor={(item) => item._id}
                style={styles.profilesContainer}
                contentContainerStyle={styles.profilesContentContainer}
                numColumns={isPhone ? 2 : 1}
                horizontal={!isPhone}
                ListEmptyComponent={() => (
                    <View style={styles.profilesContainer} />
                )}
                scrollEnabled={false}
            />
        ),
        [profileData, renderProfileItem],
    );

    const renderDescriptionComponent = useCallback(
        () => (
            <>
                <ScrollView
                    style={styles.descriptionContentContainer}
                    bounces={false}
                >
                    <View style={styles.descriptionContainer}>
                        <Text
                            category={ETextCategory.body1}
                            color={ETextColor.light_grey}
                            style={styles.description}
                            testID={PROFILE_SELECTION_DESCRIPTION}
                            accessibilityLabel={t(
                                PROFILE_PROFILE_SELECTION_DESCRIPTION,
                            )}
                            accessibilityRole="text"
                            {...(isWeb && { tabIndex: 0 })}
                            accessible
                            center
                        >
                            {t(PROFILE_PROFILE_SELECTION_DESCRIPTION)}
                        </Text>
                    </View>
                    <View
                        style={
                            profiles.length === 1 &&
                            isPhone &&
                            styles.profilesWrapper
                        }
                    >
                        {renderProfiles()}
                    </View>
                </ScrollView>
                {isPhone && !isRootNavigator && (
                    <View style={styles.buttonBackground} />
                )}
            </>
        ),
        [profiles?.length, renderProfiles, isRootNavigator, t],
    );

    const renderCloseComponent = useCallback(
        () =>
            selectedProfile && !isManageFlowEnabled && !isRootNavigator ? (
                <SvgButton
                    Svg={CloseIcon}
                    style={[styles.closeButton, { top }]}
                    svgStyle={styles.closeSvg}
                    onPress={handleClose}
                    testID={PROFILE_CLOSE_BUTTON}
                    accessibilityLabel={t(ACCESSIBILITY_CLOSE_BUTTON)}
                    accessibilityRole={Platform.select({
                        web: 'imagebutton',
                        default: 'button',
                    })}
                    accessible
                />
            ) : null,
        [
            handleClose,
            isManageFlowEnabled,
            top,
            selectedProfile,
            isRootNavigator,
        ],
    );

    useFocusEffect(
        useCallback(() => {
            debouncedProfilesFetch();
        }, [debouncedProfilesFetch]),
    );

    useFocusEffect(
        useCallback(() => {
            if (
                (editProfileRequestStatus === RequestStatus.SUCCESS ||
                    deleteProfileRequestStatus === RequestStatus.SUCCESS ||
                    editProfileRequestCode === 8123 ||
                    deleteProfileRequestCode === 8127) &&
                isManageFlowEnabled
            ) {
                setIsManageFlowEnabled(false);
                dispatch(editProfileRequestReset());
                dispatch(deleteProfileRequestReset());
            }
        }, [
            editProfileRequestStatus,
            deleteProfileRequestStatus,
            isManageFlowEnabled,
            deleteProfileRequestCode,
            editProfileRequestCode,
            dispatch,
        ]),
    );

    useFocusEffect(
        useCallback(() => {
            handleGetFTIIndicator();
        }, [handleGetFTIIndicator]),
    );

    useFocusEffect(
        useCallback(() => {
            return () => {
                selectedProfileIdScreenBlurRef.current = selectedProfileId;
            };
        }, [selectedProfileId]),
    );

    useFocusEffect(
        useCallback(() => {
            return () => {
                if (!selectedProfile) {
                    dispatch(getEntitlements({ autoRetry: true }));
                }
            };
        }, [selectedProfile]),
    );

    useEffect(() => {
        dispatch(
            endSentryTransaction(
                SENTRY_TRANSACTIONS.AppStartToProfileSelection,
            ),
        );
    }, []);

    useBackHandler(handleHardwareBackPress);

    useEffect(() => {
        if (selectedProfile && isWeb) {
            handleUpdateSelectedProfileAvatar(selectedProfile.iconName);
        }
    }, [selectedProfile, handleUpdateSelectedProfileAvatar]);

    useEffect(() => {
        if (Platform.OS === 'ios') {
            navigation.setOptions({ gestureEnabled: !!selectedProfile });
        }
    }, [selectedProfile, navigation]);

    useEffect(() => {
        if (isInternetConnected && !prevIsInternetConnected) {
            debouncedProfilesFetch();
        }
    }, [isInternetConnected, prevIsInternetConnected, debouncedProfilesFetch]);

    useEffect(() => {
        if (collectionSuspensionActive) {
            dispatch(setSuspensionType(SUSPENSION_TYPES.COLLECTION));
        }
    }, [collectionSuspensionActive, dispatch]);

    return (
        <SafeAreaView style={styles.container} edges={edges}>
            <DialogBody
                style={[
                    styles.dialog,
                    {
                        paddingTop:
                            top + (isTabletOrWeb ? 0 : PHONE_TOP_PADDING),
                    },
                ]}
                title={PROFILE_PROFILE_SELECTION_TITLE}
                titleStyle={styles.titleStyle}
                titleTestID={PROFILE_SELECTION_TITLE}
                DescriptionComponent={renderDescriptionComponent}
                actionButtonTitle={
                    isRootNavigator || isKidsProfileActive
                        ? undefined
                        : activeButtonText
                }
                actionButtonFillColor={primary}
                actionButtonStyle={[styles.button, { borderColor: primary }]}
                actionButtonTextStyle={styles.buttonText}
                actionButtonTestID={
                    isRootNavigator ? undefined : activeButtonTestId
                }
                handleActionButtonPress={handleActionButtonPress}
                forceTitleFocus={screenReaderEnabled}
            />
            {renderCloseComponent()}

            <GenericPinDialog
                appState={appState}
                visible={showPinDialog}
                onClose={handlePinControlClose}
                onSuccess={handlePinControlChecked}
                wasFullScreen
                modal={!isWeb}
            />

            <ErrorDialog
                visible={isInternetConnected && showErrorDialog}
                animationType="fade"
                title={errorKeys.title}
                description={errorKeys.description}
                actionButtonTitle={errorKeys.actionButtonText}
                actionButtonAccessibilityLabel={errorKeys.actionButtonText}
                actionButtonOnPress={handleRetryProfileListFetch}
                errorCode={`${
                    profileListErrorCode || changeProfileErrorCode || ''
                }`}
                cancelButtonTitle={OK_BUTTON}
                cancelButtonAccessibilityLabel={OK_BUTTON}
                cancelButtonFillColor={primary}
                cancelButtonStyle={[styles.button, { borderColor: primary }]}
                actionButtonStyle={styles.button}
                cancelButtonTextStyle={styles.buttonText}
                cancelButtonOnPress={handleCloseErrorDialog}
                errorStatus={profileListErrorStatus || changeProfileErrorStatus}
                actionButtonBordered
                revertButtons
            />

            <ErrorDialog
                visible={isInternetConnected && showProfileChangeErrorDialog}
                animationType="fade"
                title={t(DIALOG_ERR_GENERIC_TITLE)}
                description={t(DIALOG_ERR_GENERIC_BODY)}
                actionButtonTitle={t(OK_BUTTON)}
                actionButtonAccessibilityLabel={t(OK_BUTTON)}
                actionButtonOnPress={handleCloseProfileChangeError}
                actionButtonStyle={styles.button}
                cancelButtonTextStyle={styles.buttonText}
                cancelButtonOnPress={handleCloseProfileChangeError}
                actionButtonBordered
            />
        </SafeAreaView>
    );
}

export default ProfilesPage;
